import { snapdom, type CaptureOptions } from '@zumer/snapdom';
import { zip } from 'fflate';
import React from 'react';
import { Document, Page, Image, StyleSheet, pdf } from '@react-pdf/renderer';
import { base64ToBlob } from '@umu/umu-util';
import type {
    IDownloadOptions,
    ISingleDownloadOptions,
    IDownloadResult,
    IPackageDownloadResult,
} from '../interface/download';

/**
 * 生成带时间戳的文件名
 */
function generateFileName(prefix: string = 'certificate', extension: string): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    return `${prefix}-${timestamp}.${extension}`;
}

/**
 * 下载 Blob 文件
 */
function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * 将 HTMLImageElement 转换为 data URL
 */
function imageElementToDataUrl(
    img: HTMLImageElement,
    type: 'image/png' | 'image/jpeg' = 'image/png',
    quality?: number,
): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const handleLoad = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.drawImage(img, 0, 0);
            if (type === 'image/jpeg' && quality !== undefined) {
                resolve(canvas.toDataURL(type, quality));
            } else {
                resolve(canvas.toDataURL(type));
            }
        };

        if (img.complete) {
            handleLoad();
        } else {
            img.onload = handleLoad;
            img.onerror = reject;
        }
    });
}

/**
 * 下载 SVG 文件
 * @param options 下载选项
 * @returns Promise<IDownloadResult> 返回执行时间（毫秒）
 */
export async function downloadSvg(options: ISingleDownloadOptions): Promise<IDownloadResult> {
    const startTime = performance.now();
    const { svgContent, fileNamePrefix = 'certificate', fileName } = options;

    if (!svgContent) {
        throw new Error('SVG content is required for SVG download');
    }

    try {
        const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
        const finalFileName = fileName ? `${fileName}.svg` : generateFileName(fileNamePrefix, 'svg');
        downloadBlob(svgBlob, finalFileName);
        const duration = performance.now() - startTime;
        return { duration };
    } catch (error) {
        console.error('Failed to download SVG:', error);
        throw error;
    }
}

/**
 * 下载 PNG 文件
 * @param options 下载选项
 * @returns Promise<IDownloadResult> 返回执行时间（毫秒）
 */
export async function downloadPng(options: ISingleDownloadOptions): Promise<IDownloadResult> {
    const startTime = performance.now();
    const { svgElement, fileNamePrefix = 'certificate', scale = 2, fileName } = options;

    if (!svgElement) {
        throw new Error('SVG element is required for PNG download');
    }

    try {
        const captureResult = await snapdom(svgElement);
        const pngImg = await captureResult.toPng({
            scale,
        } as CaptureOptions);

        if (!pngImg) {
            throw new Error('Failed to generate PNG image');
        }

        const pngDataUrl = await imageElementToDataUrl(pngImg, 'image/png');
        const pngBlob = base64ToBlob(pngDataUrl, 'image/png');
        const finalFileName = fileName ? `${fileName}.png` : generateFileName(fileNamePrefix, 'png');
        downloadBlob(pngBlob, finalFileName);
        const duration = performance.now() - startTime;
        return { duration };
    } catch (error) {
        console.error('Failed to download PNG:', error);
        throw error;
    }
}

/**
 * 下载 JPEG 文件
 * @param options 下载选项
 * @returns Promise<IDownloadResult> 返回执行时间（毫秒）
 */
export async function downloadJpeg(options: ISingleDownloadOptions): Promise<IDownloadResult> {
    const startTime = performance.now();
    const { svgElement, fileNamePrefix = 'certificate', jpegQuality = 0.95, scale = 2, fileName } = options;

    if (!svgElement) {
        throw new Error('SVG element is required for JPEG download');
    }

    try {
        const captureResult = await snapdom(svgElement);
        const jpegImg = await captureResult.toJpg({
            quality: jpegQuality,
            scale,
        } as CaptureOptions);

        if (!jpegImg) {
            throw new Error('Failed to generate JPEG image');
        }

        const jpegDataUrl = await imageElementToDataUrl(jpegImg, 'image/jpeg', jpegQuality);
        const jpegBlob = base64ToBlob(jpegDataUrl, 'image/jpeg');
        const finalFileName = fileName ? `${fileName}.jpeg` : generateFileName(fileNamePrefix, 'jpeg');
        downloadBlob(jpegBlob, finalFileName);
        const duration = performance.now() - startTime;
        return { duration };
    } catch (error) {
        console.error('Failed to download JPEG:', error);
        throw error;
    }
}

/**
 * 下载 PDF 文件
 * @param options 下载选项
 * @returns Promise<IDownloadResult> 返回执行时间（毫秒）
 */
export async function downloadPdf(options: ISingleDownloadOptions): Promise<IDownloadResult> {
    const startTime = performance.now();
    const { svgElement, fileNamePrefix = 'certificate', scale = 2, fileName } = options;

    if (!svgElement) {
        throw new Error('SVG element is required for PDF download');
    }

    try {
        // 先获取 PNG 图片用于生成 PDF
        const captureResult = await snapdom(svgElement);
        const pngImg = await captureResult.toPng({
            scale,
        } as CaptureOptions);

        if (!pngImg) {
            throw new Error('Failed to generate PNG image for PDF');
        }

        const pngDataUrl = await imageElementToDataUrl(pngImg, 'image/png');

        // 将像素转换为点（1 px = 1 pt，PDF 使用点作为单位）
        const widthInPt = pngImg.naturalWidth;
        const heightInPt = pngImg.naturalHeight;

        // 创建 PDF 文档组件
        const PdfDocument = (
            <Document>
                <Page
                    size={[widthInPt, heightInPt]}
                    style={
                        StyleSheet.create({
                            page: {
                                padding: 0,
                                margin: 0,
                            },
                        }).page
                    }
                >
                    <Image
                        src={pngDataUrl}
                        style={{
                            width: widthInPt,
                            height: heightInPt,
                        }}
                    />
                </Page>
            </Document>
        );

        // 渲染 PDF 为 Blob，再通过 arrayBuffer() 强制完整读取所有字节。
        // @react-pdf/renderer 的 toBlob() 返回流式 Blob，Promise 在流开始时即 resolve，
        // 若直接 downloadBlob 后立即 revokeObjectURL，浏览器拿到的是未完成的流，导致下载延迟。
        const pdfBlob = await pdf(PdfDocument).toBlob();
        const pdfBuffer = await pdfBlob.arrayBuffer();
        const materializedBlob = new Blob([pdfBuffer], { type: 'application/pdf' });
        const finalFileName = fileName ? `${fileName}.pdf` : generateFileName(fileNamePrefix, 'pdf');
        downloadBlob(materializedBlob, finalFileName);
        const duration = performance.now() - startTime;
        return { duration };
    } catch (error) {
        console.error('Failed to download PDF:', error);
        throw error;
    }
}

/**
 * 下载证书压缩包（包含 SVG、PNG、JPEG、PDF 四种格式）
 * @param options 下载选项
 * @returns Promise<IPackageDownloadResult> 返回详细执行时间（毫秒）
 */
export async function downloadCertificatePackage(options: IDownloadOptions): Promise<IPackageDownloadResult> {
    const startTime = performance.now();
    const {
        svgContent,
        svgElement,
        fileNamePrefix = 'certificate',
        jpegQuality = 0.95,
        zipCompressionLevel = 6,
    } = options;

    // 参数校验
    if (!svgContent || !svgElement) {
        console.error('SVG content or element is required');
        throw new Error('SVG content or element is required');
    }

    try {
        // 生成默认文件名（如果没有传入任何文件名）
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const baseFileName = `${fileNamePrefix}-${timestamp}`;

        // 1. 生成 SVG 文件
        const svgStartTime = performance.now();
        const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
        const svgDuration = performance.now() - svgStartTime;

        // 2. 并行生成 PNG 和 JPEG（使用 snapdom）
        const captureResult = await snapdom(svgElement);

        // 分别记录 PNG 和 JPEG 的完整生成时间（包括捕获和转换）
        const pngStartTime = performance.now();
        const pngImg = await captureResult
            .toPng({
                scale: 2, // 提高清晰度
            } as CaptureOptions)
            .catch((error: unknown) => {
                console.error('Failed to generate PNG:', error);
                return null;
            });

        if (!pngImg) {
            throw new Error('Failed to generate PNG image');
        }
        const pngDataUrl = await imageElementToDataUrl(pngImg, 'image/png');
        const pngDuration = performance.now() - pngStartTime;

        const jpegStartTime = performance.now();
        const jpegImg = await captureResult
            .toJpg({
                quality: jpegQuality,
                scale: 2,
            } as CaptureOptions)
            .catch((error: unknown) => {
                console.error('Failed to generate JPEG:', error);
                return null;
            });

        if (!jpegImg) {
            throw new Error('Failed to generate JPEG image');
        }
        const jpegDataUrl = await imageElementToDataUrl(jpegImg, 'image/jpeg', jpegQuality);
        const jpegDuration = performance.now() - jpegStartTime;

        // 3. 将 base64 DataURL 转换为 Blob
        const pngBlob = base64ToBlob(pngDataUrl, 'image/png');
        const jpegBlob = base64ToBlob(jpegDataUrl, 'image/jpeg');

        // 4. 生成 PDF（使用 @react-pdf/renderer 和 PNG 图片创建 PDF）
        const pdfStartTime = performance.now();
        const widthInPt = pngImg.naturalWidth;
        const heightInPt = pngImg.naturalHeight;

        // 创建 PDF 文档组件
        const PdfDocument = (
            <Document>
                <Page
                    size={[widthInPt, heightInPt]}
                    style={
                        StyleSheet.create({
                            page: {
                                padding: 0,
                                margin: 0,
                            },
                        }).page
                    }
                >
                    <Image
                        src={pngDataUrl}
                        style={{
                            width: widthInPt,
                            height: heightInPt,
                        }}
                    />
                </Page>
            </Document>
        );

        // 渲染 PDF 为 Blob（浏览器端使用 pdf().toBlob()）
        const pdfBlob = await pdf(PdfDocument).toBlob();
        const pdfDuration = performance.now() - pdfStartTime;

        // 5. 将 Blob 转换为 Uint8Array（用于 FFlate）
        const blobConversionStartTime = performance.now();
        const [svgArray, pngArray, jpegArray, pdfArray] = await Promise.all([
            svgBlob.arrayBuffer().then((buf) => new Uint8Array(buf)),
            pngBlob.arrayBuffer().then((buf) => new Uint8Array(buf)),
            jpegBlob.arrayBuffer().then((buf) => new Uint8Array(buf)),
            pdfBlob.arrayBuffer().then((buf) => new Uint8Array(buf)),
        ]);
        const blobConversionDuration = performance.now() - blobConversionStartTime;

        // 6. 创建 ZIP 压缩包（使用 FFlate）
        const zipStartTime = performance.now();
        const zipBlob = await new Promise<Blob>((resolve, reject) => {
            const svgFileName = options.fileNames?.svg || `${baseFileName}.svg`;
            const pngFileName = options.fileNames?.png || `${baseFileName}.png`;
            const jpegFileName = options.fileNames?.jpeg || `${baseFileName}.jpeg`;
            const pdfFileName = options.fileNames?.pdf || `${baseFileName}.pdf`;

            const files: Record<string, Uint8Array> = {
                [svgFileName.includes('.') ? svgFileName : `${svgFileName}.svg`]: svgArray,
                [pngFileName.includes('.') ? pngFileName : `${pngFileName}.png`]: pngArray,
                [jpegFileName.includes('.') ? jpegFileName : `${jpegFileName}.jpeg`]: jpegArray,
                [pdfFileName.includes('.') ? pdfFileName : `${pdfFileName}.pdf`]: pdfArray,
            };

            zip(
                files,
                {
                    level: (zipCompressionLevel ?? 6) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9, // 压缩级别 0-9
                },
                (err, data) => {
                    if (err) {
                        reject(err);
                        return;
                    }
                    resolve(new Blob([data.buffer as ArrayBuffer], { type: 'application/zip' }));
                },
            );
        });
        const zipDuration = performance.now() - zipStartTime;

        // 7. 触发下载
        const zipFileName = options.zipFileName || generateFileName(fileNamePrefix, 'zip');
        const finalZipFileName = zipFileName.includes('.') ? zipFileName : `${zipFileName}.zip`;
        downloadBlob(zipBlob, finalZipFileName);
        const duration = performance.now() - startTime;

        // 收集文件大小信息
        const svgSize = svgBlob.size;
        const pngSize = pngBlob.size;
        const jpegSize = jpegBlob.size;
        const pdfSize = pdfBlob.size;
        const zipSize = zipBlob.size;
        const totalSize = svgSize + pngSize + jpegSize + pdfSize;

        // 计算压缩效率
        const compressionRatio = totalSize / zipSize;
        const savings = totalSize - zipSize;
        const savingsPercentage = (savings / totalSize) * 100;

        // 收集图片尺寸信息
        const dimensions = {
            png: { width: pngImg.naturalWidth, height: pngImg.naturalHeight },
            jpeg: { width: jpegImg.naturalWidth, height: jpegImg.naturalHeight },
            pdf: { width: widthInPt, height: heightInPt },
        };

        // 计算处理速度（MB/s）
        const pngSpeed = pngSize / 1024 / 1024 / (pngDuration / 1000);
        const jpegSpeed = jpegSize / 1024 / 1024 / (jpegDuration / 1000);
        const pdfSpeed = pdfSize / 1024 / 1024 / (pdfDuration / 1000);
        const zipSpeed = totalSize / 1024 / 1024 / (zipDuration / 1000);

        // 质量设置
        const quality = {
            jpegQuality,
            pngScale: 2,
            jpegScale: 2,
        };

        return {
            duration,
            timings: {
                svg: svgDuration,
                png: pngDuration,
                jpeg: jpegDuration,
                pdf: pdfDuration,
                blobConversion: blobConversionDuration,
                zip: zipDuration,
            },
            fileSizes: {
                svg: svgSize,
                png: pngSize,
                jpeg: jpegSize,
                pdf: pdfSize,
                zip: zipSize,
                total: totalSize,
            },
            compression: {
                ratio: compressionRatio,
                savings,
                savingsPercentage,
                level: zipCompressionLevel,
            },
            dimensions,
            speeds: {
                png: pngSpeed,
                jpeg: jpegSpeed,
                pdf: pdfSpeed,
                zip: zipSpeed,
            },
            quality,
        };
    } catch (error) {
        console.error('Failed to download certificate package:', error);
        throw error;
    }
}
